import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import ApiService, { initialAuthState } from '../../../services/ApiService';
import './OrderItemDetails.css';
import { IoMdArrowRoundBack } from "react-icons/io";

// ✅ Status Badge
const StatusBadge = ({ status }) => (
    <span className={`admin-order-item-status ${status.replaceAll(' ', '-')}`}>
        {status.replaceAll('_', ' ')}
    </span>
);

// ✅ Branch Reassign Modal
const BranchModal = ({ show, onClose, branches, onSelect }) => {
    const [search, setSearch] = useState("");

    if (!show) return null;

    const filteredBranches = branches.filter(b =>
        b.branchName.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className=" branch-modal-overlay amz-modal-overlay">

            <div className="amz-modal large">

                {/* HEADER */}
                <div className="amz-modal-header">
                    <h3>Select Branch</h3>
                    <button onClick={onClose}>✕</button>
                </div>

                {/* SEARCH */}
                <div className="amz-search">
                    <input
                        type="text"
                        placeholder="Search branch..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {/* TABLE HEADER */}
                <div className="amz-table-header">
                    <span>Branch</span>
                    <span>Location</span>
                    <span>Stock</span>
                    <span>Action</span>
                </div>

                {/* LIST */}
                <div className="amz-table-body">
                    {filteredBranches.length > 0 ? (
                        filteredBranches.map(branch => {
                            const isLowStock = branch.availableQuantity <= 0;

                            return (
                                <div key={branch.branchId} className="amz-table-row">

                                    <span className="branch-name">
                                        {branch.branchName}
                                    </span>

                                    <span>
                                        {branch.city || "-"}, {branch.state || "-"}
                                    </span>

                                    <span className={isLowStock ? "stock low" : "stock ok"}>
                                        {branch.availableQuantity}
                                    </span>

                                    <span>
                                        <button
                                            className="amz-btn primary"
                                            disabled={isLowStock}
                                            onClick={() => onSelect(branch.branchId)}
                                        >
                                            Assign
                                        </button>
                                    </span>

                                </div>
                            );
                        })
                    ) : (
                        <div className="amz-empty">No branches found</div>
                    )}
                </div>

            </div>
        </div>
    );
};

const STATUS_FLOW = {
    // confirmed: ['packed', 'cancelled'],
    // packed: ['shipped'],
    // shipped: ['out_for_delivery'],
    // out_for_delivery: ['delivered'],
    // delivered: [],
    replacement_requested: ['approved', 'rejected'],
    replacement_approved: ['pickup_scheduled'],
    pickup_scheduled: ['picked_up'],
    picked_up: ['replaced'],
    // replaced: ['replaced'],
    // replaced: [],
    // cancelled: [],
    // return_approved: ['return_in_progress'],
    // return_in_progress: ['returned'],
    // returned: [],
};

const OrderItemDetails = () => {
    const { itemId } = useParams();
    const navigate = useNavigate();

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState([]);
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [retryingRefundId, setRetryingRefundId] = useState(null);

    const [deliveryDate, setDeliveryDate] = useState('');
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);

    const [showReplacementModal, setShowReplacementModal] = useState(false);
    const [activeReplacementRequestId, setActiveReplacementRequestId] = useState(null);
    const [replacementAction, setReplacementAction] = useState('');
    const [replacementReason, setReplacementReason] = useState('');


    const openReplacementAction = (requestId, action) => {
        setActiveReplacementRequestId(requestId);
        setReplacementAction(action); // approved / rejected
        setReplacementReason("");
        setShowReplacementModal(true);
    };

    const updateDeliveryDate = async () => {
        try {
            await ApiService.post(`/order-item/adminUpdatedeliveryDate`, {
                orderItemId: Number(itemId),
                deliveredAt: deliveryDate
            });

            alert("Delivery date updated successfully");
            setShowDeliveryModal(false);
            fetchItem();
        } catch (err) {
            console.error(err);
            alert("Failed to update delivery date");
        }
    };


    const handlePickupSchedule = (status) => {
        console.log("triggered", showPickupModal, status)

        setShowPickupModal(true);
    };

    const staffDbId = localStorage.getItem('id')

    const [pickupDate, setPickupDate] = useState('');
    const [showPickupModal, setShowPickupModal] = useState(false);

    // ✅ Fetch item details
    const fetchItem = async () => {
        setLoading(true);
        try {
            const res = await ApiService.post('/order-item/getOrderItemById', {
                id: Number(itemId),
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            if (res.status) setItem(res.data);
        } catch (err) {
            console.error('Error fetching item:', err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Fetch available branches
    const fetchBranches = async () => {
        if (!item?.device?.id) return;
        try {
            const res = await ApiService.post('/branch-stock/getAvailableBranches', {
                deviceId: item.device.id,
                quantity: item.quantity,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            if (res.status) setBranches(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchItem();
    }, [itemId]);

    // ✅ Update Status
    const updateOrderItemStatus = async (newStatus) => {
        if (!window.confirm(`Are you sure you want to mark this item as "${newStatus.replaceAll('_', ' ')}"?`)) return;
        try {
            await ApiService.post('/order-item/adminUpdateStatus', {
                id: item.id,
                status: newStatus,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            fetchItem();
        } catch (err) {
            console.error('Error updating status:', err);
            alert('Failed to update status.');
        }
    };

    const submitReplacementAction = async () => {
        try {
            await ApiService.patch(`/replacements/${activeReplacementRequestId}`, {
                status: replacementAction,
                adminRemark: replacementReason,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });

            setShowReplacementModal(false);
            fetchItem();
        } catch (err) {
            console.error(err);
            alert("Failed to update replacement");
        }
    };

    const updateStatus = async (newStatus) => {
        const replacementId = item?.replacementRequests?.[0]?.id;

        if (!replacementId) {
            alert("No replacement request found");
            return;
        }
        if (!window.confirm(`Are you sure you want to mark this item as "${newStatus.replaceAll('_', ' ')}"?`)) return;

        try {
            await ApiService.patch(`/replacements/${replacementId}`, {
                id: item.id,
                status: newStatus,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            fetchItem();
        } catch (err) {
            console.error('Error updating status:', err);
            alert('Failed to update status.');
        }
    };

    const submitPickup = async () => {
        const replacementId = item?.replacementRequests?.[0]?.id;

        if (!replacementId) {
            alert("No replacement request found");
            return;
        }
        try {
            await ApiService.patch(`/replacements/${replacementId}`, {
                status: 'pickup_scheduled',
                pickupDate: pickupDate
            });

            setShowPickupModal(false);
            fetchItem();

        } catch (err) {
            console.error(err);
        }
    };

    // ✅ Cancel Item
    const cancelItem = async () => {
        const reason = prompt('Enter cancel reason:');
        if (!reason) return;
        try {
            await ApiService.post('/order-item/adminUpdateStatus', {
                id: item.id,
                reason,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            fetchItem();
        } catch (err) {
            console.error('Error cancelling item:', err);
            alert(err.response?.data?.message || 'Error cancelling item');
        }
    };

    // ✅ Create Shipment
    const createShipment = async () => {
        try {
            const res = await ApiService.post('/shipment/auto-create', {
                orderId: item.order.id,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            if (res.status) navigate(`/admin/shipments/${res.data.id}`);
        } catch (err) {
            console.error('Error creating shipment:', err);
            alert('Failed to create shipment.');
        }
    };

    // ✅ Branch Modal Handling
    const openBranchModal = async () => {
        await fetchBranches();
        setShowBranchModal(true);
    };

    const handleBranchSelect = async (branchId) => {
        try {
            await ApiService.post('/order-item/reassignBranch', {
                id: item.id,
                branchId,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            alert('Branch reassigned successfully');
            setShowBranchModal(false);
            fetchItem();
        } catch (err) {
            alert(err.response?.data?.message || 'Error reassigning branch');
        }
    };

    if (loading) return <div className="admin-order-item-loader">Loading...</div>;
    if (!item) return <div className="admin-order-item-error">Item not found</div>;

    const {
        deviceName,
        deviceModel,
        quantity,
        price,
        discount,
        finalPrice,
        totalTaxAmount,
        totalPrice,
        cgstAmount,
        cgstPercent,
        sgstAmount,
        sgstPercent,
        igstAmount,
        igstPercent,
        networkType,
        subscriptionType,
        itemStatus,
        branchId,
        branchName,
        shipmentId,
    } = item;

    // ✅ Determine allowed actions based on current status
    const allowedActions = STATUS_FLOW[itemStatus] || [];

    const handleReplacementAction = async (requestId, action) => {
        try {
            await ApiService.post('/replacement/updateStatus', {
                id: requestId,
                status: action,
                companyCode: initialAuthState.companyCode,
                unitCode: initialAuthState.unitCode,
            });
            fetchItem(); // refresh item to show updated request
        } catch (err) {
            console.error('Error updating replacement request:', err);
            alert('Failed to update replacement request.');
        }
    };

    const handleRetryRefund = async (refundId) => {
        if (!window.confirm("Are you sure you want to retry this refund?")) return;

        try {
            setRetryingRefundId(refundId);
            const res = await ApiService.post(`/Refund/${refundId}/retry`);
            alert(`Refund retried successfully. Status: ${res.data.refundStatus}`);
            fetchItem(); // refresh item to show updated refund status
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to retry refund');
        } finally {
            setRetryingRefundId(null);
        }
    };

    const handleDownload = async (url, index) => {
        try {
            const response = await fetch(url, { mode: "cors" });
            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `replacement-image-${index + 1}.jpg`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);

            // fallback
            window.open(url, "_blank");
        }
    };


    const formatDate = (date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/all-orders');
        }
    };

    return (
        <>
            <div className="admin-order-item-page">
                <div className="admin-order-item-topbar">
                    <button onClick={handleBack} className="admin-order-item-back-btn">
                        <IoMdArrowRoundBack /> Back
                    </button>

                    <div className="admin-order-item-top-actions">
                        {shipmentId && (
                            <button
                                className="admin-order-item-outline-btn"
                                onClick={() => navigate(`/admin/shipments/${shipmentId}`)}
                            >
                                View Shipment
                            </button>
                        )}
                    </div>
                </div>

                <section className="admin-order-item-hero">
                    <div>
                        <p className="admin-order-item-eyebrow">Order Item Details</p>
                        <h1>{deviceName}</h1>
                        <div className="admin-order-item-meta">
                            <span>Order: {item.order.orderNumber}</span>
                            <span>Item: {item.orderItemNumber}</span>
                            <span>Qty: {quantity}</span>
                        </div>
                    </div>

                    <StatusBadge status={itemStatus} />
                </section>

                <div className="admin-order-item-layout">
                    <main className="admin-order-item-main">
                        <section className="admin-order-item-card">
                            <div className="admin-order-item-card-head">
                                <h3>Shipping From</h3>

                                {itemStatus === "confirmed" && (
                                    <button className="admin-order-item-link-btn"
                                        disabled={!['confirmed', 'replacement_approved'].includes(item.itemStatus)}
                                        onClick={openBranchModal}
                                    >
                                        Change Branch
                                    </button>
                                )}
                            </div>

                            <div className="admin-order-item-info-grid">
                                <div>
                                    <span>Branch</span>
                                    <strong>{branchName || "Not Assigned"}</strong>
                                </div>
                                <div>
                                    <span>City</span>
                                    <strong>{item?.branch?.city || "—"}</strong>
                                </div>
                                <div>
                                    <span>State</span>
                                    <strong>{item?.branch?.state || "—"}</strong>
                                </div>
                            </div>
                        </section>

                        <section className="admin-order-item-card">
                            <div className="admin-order-item-card-head">
                                <h3>Delivery</h3>

                                {itemStatus !== "delivered" && itemStatus !== "cancelled" && (
                                    <button
                                        className="admin-order-item-link-btn"
                                        onClick={() => {
                                            setDeliveryDate(item?.deliveredAt || "");
                                            setShowDeliveryModal(true);
                                        }}
                                    >
                                        Modify Delivery
                                    </button>
                                )}
                            </div>

                            {item ? (
                                <div className="admin-order-item-info-grid">
                                    <div>
                                        <span>Status</span>
                                        <strong className={`amz-status ${itemStatus}`}>{itemStatus.replaceAll("_", " ")}</strong>
                                    </div>
                                    <div>
                                        <span>Expected Delivery</span>
                                        <strong>{formatDate(item?.deliveredAt) || "Not Set"}</strong>
                                    </div>
                                    <div>
                                        <span>Shipment ID</span>
                                        <strong>{item?.shipmentId || "Not Created"}</strong>
                                    </div>
                                </div>
                            ) : (
                                <p className="amz-muted">No shipment created yet</p>
                            )}

                            {showDeliveryModal && (
                                <div className="amz-modal-overlay">

                                    <div className="amz-modal">

                                        <div className="amz-modal-header">
                                            <h3>Update Delivery Date</h3>
                                            <button onClick={() => setShowDeliveryModal(false)}>✕</button>
                                        </div>

                                        <div className="amz-info-box">
                                            <div className="amz-row">
                                                <span>Current Delivery</span>
                                                <strong>
                                                    {item?.deliveredAt
                                                        ? formatDate(item.deliveredAt)
                                                        : "Not Set"}
                                                </strong>
                                            </div>

                                            <div className="amz-row">
                                                <span>Status</span>
                                                <strong>{itemStatus.replaceAll("_", " ")}</strong>
                                            </div>
                                        </div>

                                        <div className="amz-field">
                                            <label>New Delivery Date</label>
                                            <input
                                                type="date"
                                                value={deliveryDate}
                                                min={new Date().toISOString().split("T")[0]} // 🚀 prevent past
                                                onChange={(e) => setDeliveryDate(e.target.value)}
                                            />
                                        </div>

                                        <div className="amz-field">
                                            <label>Reason for Change</label>
                                            <textarea
                                                placeholder="Enter reason (required)"
                                                rows={3}
                                            />
                                        </div>

                                        <div className="amz-warning">
                                            Changing delivery date will notify the customer.
                                        </div>

                                        <div className="amz-modal-actions">
                                            <button
                                                className="amz-btn primary"
                                                onClick={updateDeliveryDate}
                                            >
                                                Update Delivery
                                            </button>

                                            <button onClick={() => setShowDeliveryModal(false)}>
                                                Cancel
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            )}

                        </section>

                        {item.replacementRequests?.length > 0 && (
                            <section className="admin-order-item-card">
                                <div className="admin-order-item-card-head">
                                    <h3>Replacement Requests</h3>
                                    <span className="admin-order-item-count">
                                        {item.replacementRequests.length}
                                    </span>
                                </div>

                                {item.replacementRequests.map((req) => (
                                    <div key={req.id} className="admin-order-item-replacement">
                                        <div className="admin-order-item-replacement-head">
                                            <div>
                                                <strong>Request #{req.id}</strong>
                                                <span>{req.status.replaceAll("_", " ")}</span>
                                            </div>

                                            {/* {req.status === "requested" && (
                                                <div className="admin-order-item-action-row">
                                                    <button
                                                        className="admin-order-item-success-btn"
                                                        onClick={() =>
                                                            openReplacementAction(req.id, "replacement_approved")
                                                        }
                                                    >
                                                        Approve
                                                    </button>

                                                    <button
                                                        className="admin-order-item-danger-btn"
                                                        onClick={() =>
                                                            openReplacementAction(req.id, "replacement_rejected")
                                                        }
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )} */}
                                        </div>

                                        <div className="admin-order-item-info-grid">
                                            <div>
                                                <span>Reason</span>
                                                <strong>{req.reason || "—"}</strong>
                                            </div>
                                            <div>
                                                <span>Customer Comment</span>
                                                <strong>{req.comment || "—"}</strong>
                                            </div>
                                            <div>
                                                <span>Admin Response</span>
                                                <strong>{req.adminRemark || "Pending"}</strong>
                                            </div>
                                        </div>

                                        {req.images?.length > 0 && (
                                            <div className="amz-replacement-images">
                                                {req.images.map((img, i) => (
                                                    <div key={i} className="amz-image-box">
                                                        <img src={img} alt={`replacement-${i}`} />
                                                        <button onClick={() => handleDownload(img, i)}>
                                                            Download
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {item.isReplacement && (
                                            <div className="amz-replacement-link">

                                                <div>
                                                    <span className="amz-badge">Replacement Item</span>
                                                    <p>This item replaces another order item</p>
                                                </div>

                                                <button
                                                    className="amz-link-btn"
                                                    onClick={() => navigate(`/order-item/${item.parentOrderItemId}`)}
                                                >
                                                    View Original
                                                </button>

                                            </div>
                                        )}

                                        {showPickupModal && (
                                            <div className="amz-modal-overlay">
                                                <div className="amz-modal">

                                                    <h3>Schedule Pickup</h3>

                                                    <div className="amz-field">
                                                        <label>Pickup Date</label>
                                                        <input
                                                            type="date"
                                                            onChange={(e) => setPickupDate(e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="amz-modal-actions">
                                                        <button className="amz-btn primary" onClick={submitPickup}>
                                                            Confirm
                                                        </button>

                                                        <button onClick={() => setShowPickupModal(false)}>
                                                            Cancel
                                                        </button>
                                                    </div>

                                                </div>
                                            </div>
                                        )}

                                        {showReplacementModal && (
                                            <div className="amz-modal-overlay">
                                                <div className="amz-modal">

                                                    <h3>
                                                        {replacementAction === "approved"
                                                            ? "Approve Replacement"
                                                            : "Reject Replacement"}
                                                    </h3>

                                                    <div className="amz-field">
                                                        <label>Admin Remark</label>
                                                        <textarea
                                                            placeholder="Enter reason..."
                                                            value={replacementReason}
                                                            onChange={(e) => setReplacementReason(e.target.value)}
                                                            rows={4}
                                                        />
                                                    </div>

                                                    <div className="amz-modal-actions">
                                                        <button
                                                            className={`amz-btn ${replacementAction === "approved" ? "success" : "danger"}`}
                                                            onClick={submitReplacementAction}
                                                        >
                                                            Submit
                                                        </button>

                                                        <button onClick={() => setShowReplacementModal(false)}>
                                                            Cancel
                                                        </button>
                                                    </div>

                                                </div>
                                            </div>
                                        )}



                                    </div>
                                ))}
                            </section>
                        )}





                        {item?.shipmentItems?.length > 0 &&
                            ["replacement_requested", "replacement_approved", "pickup_scheduled", "picked_up"].includes(itemStatus) && (
                                <div className="admin-order-item-card">
                                    <h3>Status Actions</h3>

                                    <div className="admin-order-item-actions">
                                        {allowedActions.map((status) => (
                                            <button
                                                key={status}
                                                onClick={() => {
                                                    if (status === 'pickup_scheduled') {
                                                        handlePickupSchedule(status);
                                                        return;
                                                    }

                                                    if (itemStatus === "replacement_requested") {
                                                        const requestId = item?.replacementRequests?.[0]?.id;

                                                        if (!requestId) {
                                                            alert("No replacement request found");
                                                            return;
                                                        }

                                                        openReplacementAction(requestId, status); // ✅ FIXED
                                                        return;
                                                    }

                                                    updateStatus(status);
                                                }}
                                            >
                                                {status.replaceAll('_', ' ')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                        {/* CRITICAL ACTIONS */}
                        {['confirmed', 'placed', 'replacement_approved'].includes(itemStatus) && (
                            <div className="admin-order-item-card">
                                <h3>Critical Actions</h3>
                                <button className="admin-order-item-danger-btn" onClick={() => updateOrderItemStatus("cancelled")}>
                                    Cancel Item
                                </button>

                            </div>
                        )}

                        <div className="admin-order-item-card">
                            <h3>Refunds</h3>

                            {item.refunds && item.refunds.length > 0 ? (
                                item.refunds.map((refund) => (
                                    <div key={refund.id} className="refund-card">

                                        <div className="refund-header">
                                            <div>
                                                <h4>Refund #{refund.id}</h4>
                                                <span className={`refund-status ${refund.refundStatus}`}>
                                                    {refund.refundStatus?.replaceAll("_", " ")}
                                                </span>
                                            </div>

                                            <div className="refund-type">
                                                {refund.refundType?.toUpperCase()}
                                            </div>
                                        </div>

                                        <div className="refund-grid">

                                            <div>
                                                <span>Item Amount</span>
                                                <strong>₹{refund.itemAmount}</strong>
                                            </div>

                                            <div>
                                                <span>Gateway Fee</span>
                                                <strong>- ₹{refund.gatewayFeeDeducted}</strong>
                                            </div>

                                            <div>
                                                <span>Gateway Tax</span>
                                                <strong>- ₹{refund.gatewayTaxDeducted}</strong>
                                            </div>

                                            <div>
                                                <span>Shipping Deduction</span>
                                                <strong>- ₹{refund.shippingDeduction}</strong>
                                            </div>

                                            <div>
                                                <span>Shipping Refund Type</span>
                                                <strong>
                                                    {refund.shippingRefundType?.replaceAll("_", " ") || "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Relay Amount</span>
                                                <strong>₹{refund.relayAmount}</strong>
                                            </div>

                                            <div>
                                                <span>Processed By</span>
                                                <strong>{refund.processedBy || "-"}</strong>
                                            </div>

                                            <div>
                                                <span>Requested By</span>
                                                <strong>{refund.requestedBy || "-"}</strong>
                                            </div>

                                            <div>
                                                <span>Created On</span>
                                                <strong>
                                                    {refund.createdAt
                                                        ? new Date(refund.createdAt).toLocaleString()
                                                        : "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Processed On</span>
                                                <strong>
                                                    {refund.processedAt
                                                        ? new Date(refund.processedAt).toLocaleString()
                                                        : "Pending"}
                                                </strong>
                                            </div>

                                        </div>

                                        {(refund.reason ||
                                            refund.reasonCode ||
                                            refund.adminRemark) && (
                                                <div className="refund-remarks">

                                                    {refund.reasonCode && (
                                                        <p>
                                                            <strong>Reason Code:</strong>{" "}
                                                            {refund.reasonCode}
                                                        </p>
                                                    )}

                                                    {refund.reason && (
                                                        <p>
                                                            <strong>Reason:</strong>{" "}
                                                            {refund.reason}
                                                        </p>
                                                    )}

                                                    {refund.adminRemark && (
                                                        <p>
                                                            <strong>Admin Remark:</strong>{" "}
                                                            {refund.adminRemark}
                                                        </p>
                                                    )}

                                                </div>
                                            )}

                                        <div className="refund-final-amount">
                                            <span>Final Refund Amount</span>
                                            <strong>₹{refund.finalRefundAmount}</strong>
                                        </div>

                                        {refund.refundStatus?.toLowerCase() !== "success" && (
                                            <button
                                                className="retry-refund-btn"
                                                onClick={() => handleRetryRefund(refund.id)}
                                                disabled={retryingRefundId === refund.id}
                                            >
                                                {retryingRefundId === refund.id
                                                    ? "Retrying..."
                                                    : "Retry Refund"}
                                            </button>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p>No refunds found</p>
                            )}
                        </div>

                        {/* BRANCH MODAL */}
                        <BranchModal
                            show={showBranchModal}
                            onClose={() => setShowBranchModal(false)}
                            branches={branches}
                            onSelect={handleBranchSelect}
                        />
                    </main>



                    <aside className="admin-order-item-sidebar">
                        <section className="admin-order-item-card admin-order-item-price-card">
                            <h3>Pricing Summary</h3>

                            <div className="admin-order-item-price-row">
                                <span>Base Price</span>
                                <strong>₹{price}</strong>
                            </div>

                            <div className="admin-order-item-price-row">
                                <span>Discount ({discount}%)</span>
                                <strong>- ₹{((price * discount) / 100).toFixed(2)}</strong>
                            </div>

                            <div className="admin-order-item-price-row">
                                <span>Final Unit Price</span>
                                <strong>₹{finalPrice}</strong>
                            </div>

                            <div className="admin-order-item-price-row">
                                <span>Quantity</span>
                                <strong>x {quantity}</strong>
                            </div>

                            <div className="admin-order-item-price-row">
                                <span>Subtotal</span>
                                <strong>₹{(finalPrice * quantity).toFixed(2)}</strong>
                            </div>

                            <div className="amz-price-section">
                                <h4>Additional Charges</h4>
                                <div className="admin-order-item-price-row">
                                    <span>Shipping</span>
                                    <strong>
                                        {item?.order?.isShippingFree ? "FREE" : `₹${item?.order?.shippingCharge}`}
                                    </strong>
                                </div>
                            </div>

                            <div className="amz-price-section">
                                <h4>Discounts</h4>

                                <div className="admin-order-item-price-row">
                                    <span>Coupon Discount</span>
                                    <strong>- ₹{item?.couponDiscount || 0}</strong>
                                </div>
                            </div>

                            <div className="amz-price-section">
                                <h4>Taxes</h4>

                                {cgstAmount > 0 && (
                                    <div className="admin-order-item-price-row">
                                        <span>CGST ({cgstPercent}%)</span>
                                        <strong>₹{cgstAmount}</strong>
                                    </div>
                                )}

                                {sgstAmount > 0 && (
                                    <div className="admin-order-item-price-row">
                                        <span>SGST ({sgstPercent}%)</span>
                                        <strong>₹{sgstAmount}</strong>
                                    </div>
                                )}

                                {igstAmount > 0 && (
                                    <div className="admin-order-item-price-row">
                                        <span>IGST ({igstPercent}%)</span>
                                        <strong>₹{igstAmount}</strong>
                                    </div>
                                )}

                            </div>

                            <div className="admin-order-item-price-row">
                                <span>Total Tax</span>
                                <strong>₹{totalTaxAmount}</strong>
                            </div>

                            <div className="admin-order-item-grand-total">
                                <span>Total Payable</span>
                                <strong>₹{totalPrice}</strong>
                            </div>

                            <div className="admin-order-item-payment">
                                {item?.order?.paymentStatus || "Payment status unavailable"}
                            </div>
                        </section>
                    </aside>
                </div>


            </div>

            {/* <div className="admin-order-item-page">
                <div className="replace-request-button-container">
                    <button onClick={handleBack} className="website-back-button">
                        <IoMdArrowRoundBack /> Back
                    </button>
                </div>
                <div className="admin-order-item-card admin-order-item-header">
                    <div>
                        <h1>Order Number: {item.order.orderNumber}</h1>
                        <h1>OrderItem Number: {item.orderItemNumber}</h1>
                        <h2>{deviceName}</h2>
                        <p>{deviceModel}</p>
                    </div>
                    <StatusBadge status={itemStatus} />
                </div>

                <div className="admin-amz-section">

                    <div className="amz-block">
                        <div className="amz-block-header with-action">
                            <h3>Shipping From</h3>

                            {itemStatus === "confirmed" && (
                                <button
                                    className="amz-link-btn"
                                    onClick={openBranchModal}
                                    disabled={!['confirmed', 'replacement_approved'].includes(item.itemStatus)}
                                >
                                    Change Branch
                                </button>
                            )}
                        </div>

                        <div className="amz-row">
                            <span>Branch</span>
                            <strong>{branchName || "Not Assigned"}</strong>
                        </div>

                        <div className="amz-row">
                            <span>City</span>
                            <strong>{item?.branch?.city || "—"}</strong>
                        </div>

                        <div className="amz-row">
                            <span>State</span>
                            <strong>{item?.branch?.state || "—"}</strong>
                        </div>
                    </div>


                    <div className="amz-block">
                        <div className="amz-block-header with-action">
                            <h3>Delivery</h3>

                            {itemStatus !== "delivered" && itemStatus !== "cancelled" && (
                                <button
                                    className="amz-link-btn"
                                    onClick={() => {
                                        setDeliveryDate(item?.deliveredAt || '');
                                        setShowDeliveryModal(true);
                                    }}
                                >
                                    Modify Delivery
                                </button>
                            )}
                        </div>

                        {item ? (
                            <>
                                <div className="amz-row">
                                    <span>Status</span>
                                    <strong className={`amz-status ${itemStatus}`}>
                                        {itemStatus.replaceAll("_", " ")}
                                    </strong>
                                </div>

                                <div className="amz-row">
                                    <span>Expected Delivery</span>
                                    <strong>
                                        {formatDate(item?.deliveredAt) || "Not Set"}
                                    </strong>
                                </div>

                                <div className="amz-row">
                                    <span>Shipment ID</span>
                                    <strong>{item?.shipmentId || "Not Created"}</strong>
                                </div>
                            </>
                        ) : (
                            <p className="amz-muted">No shipment created yet</p>
                        )}
                    </div>


                    {showDeliveryModal && (
                        <div className="amz-modal-overlay">

                            <div className="amz-modal">

                                <div className="amz-modal-header">
                                    <h3>Update Delivery Date</h3>
                                    <button onClick={() => setShowDeliveryModal(false)}>✕</button>
                                </div>

                                <div className="amz-info-box">
                                    <div className="amz-row">
                                        <span>Current Delivery</span>
                                        <strong>
                                            {item?.deliveredAt
                                                ? formatDate(item.deliveredAt)
                                                : "Not Set"}
                                        </strong>
                                    </div>

                                    <div className="amz-row">
                                        <span>Status</span>
                                        <strong>{itemStatus.replaceAll("_", " ")}</strong>
                                    </div>
                                </div>

                                <div className="amz-field">
                                    <label>New Delivery Date</label>
                                    <input
                                        type="date"
                                        value={deliveryDate}
                                        min={new Date().toISOString().split("T")[0]}
                                        onChange={(e) => setDeliveryDate(e.target.value)}
                                    />
                                </div>

                                <div className="amz-field">
                                    <label>Reason for Change</label>
                                    <textarea
                                        placeholder="Enter reason (required)"

                                        rows={3}
                                    />
                                </div>

                                <div className="amz-warning">
                                    Changing delivery date will notify the customer.
                                </div>

                                <div className="amz-modal-actions">
                                    <button
                                        className="amz-btn primary"
                                        onClick={updateDeliveryDate}
                                    >
                                        Update Delivery
                                    </button>

                                    <button onClick={() => setShowDeliveryModal(false)}>
                                        Cancel
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}

                </div>

                <div className="admin-order-item-card amz-pricing">

                    <div className="amz-pricing-header">
                        <h3>Pricing Details</h3>

                        {shipmentId && (
                            <button
                                className="amz-link-btn"
                                onClick={() => navigate(`/admin/shipments/${shipmentId}`)}
                            >
                                View Shipment
                            </button>
                        )}
                    </div>

                    <div className="amz-price-section">
                        <h4>Product Charges</h4>

                        <div className="amz-row">
                            <span>Base Price</span>
                            <strong>₹{price}</strong>
                        </div>

                        <div className="amz-row">
                            <span>Product Discount ({discount}%)</span>
                            <strong>- ₹{(price * discount) / 100}</strong>
                        </div>

                        <div className="amz-row highlight">
                            <span>Final Unit Price</span>
                            <strong>₹{finalPrice}</strong>
                        </div>

                        <div className="amz-row">
                            <span>Quantity</span>
                            <strong>x {quantity}</strong>
                        </div>

                        <div className="amz-row total">
                            <span>Subtotal</span>
                            <strong>₹{(finalPrice * quantity).toFixed(2)}</strong>
                        </div>
                    </div>

                    <div className="amz-price-section">
                        <h4>Additional Charges</h4>


                        <div className="amz-row">
                            <span>Shipping</span>
                            <strong>
                                {item?.order?.isShippingFree ? "FREE" : `₹${item?.order?.shippingCharge}`}
                            </strong>
                        </div>
                    </div>


                    <div className="amz-price-section">
                        <h4>Discounts</h4>

                        <div className="amz-row">
                            <span>Coupon Discount</span>
                            <strong>- ₹{item?.couponDiscount || 0}</strong>
                        </div>
                    </div>


                    <div className="amz-price-section">
                        <h4>Taxes</h4>

                        {cgstAmount > 0 && (
                            <div className="amz-row">
                                <span>CGST ({cgstPercent}%)</span>
                                <strong>₹{cgstAmount}</strong>
                            </div>
                        )}

                        {sgstAmount > 0 && (
                            <div className="amz-row">
                                <span>SGST ({sgstPercent}%)</span>
                                <strong>₹{sgstAmount}</strong>
                            </div>
                        )}

                        {igstAmount > 0 && (
                            <div className="amz-row">
                                <span>IGST ({igstPercent}%)</span>
                                <strong>₹{igstAmount}</strong>
                            </div>
                        )}

                        <div className="amz-row total">
                            <span>Total Tax</span>
                            <strong>₹{totalTaxAmount}</strong>
                        </div>
                    </div>


                    <div className="amz-final-box">
                        <div className="amz-row grand">
                            <span>Total Payable</span>
                            <strong>₹{totalPrice}</strong>
                        </div>

                        <div className="amz-row paid">
                            <span>Payment Status</span>
                            <strong>{item?.order?.paymentStatus}</strong>
                        </div>
                    </div>

                </div>

                {item.replacementRequests?.length > 0 && (
                    <div className="amz-section">

                        <div className="amz-section-header">
                            <h3>Replacement Requests</h3>
                            <span className="amz-count">{item.replacementRequests.length}</span>
                        </div>

                        {item.replacementRequests.map((req) => (
                            <div key={req.id} className="amz-replacement-card">

                                <div className="amz-replacement-header">
                                    <div>
                                        <strong>Request #{req.id}</strong>
                                        <span className={`amz-status ${req.status}`}>
                                            {req.status.replaceAll("_", " ")}
                                        </span>
                                    </div>

                                    <div className="amz-actions-inline">
                                        {req.status === "requested" && (
                                            <>
                                                <button
                                                    className="amz-btn success"
                                                    onClick={() => openReplacementAction(req.id, "replacement_approved")}
                                                >
                                                    Approve
                                                </button>

                                                <button
                                                    className="amz-btn danger"
                                                    onClick={() => openReplacementAction(req.id, "replacement_rejected")}
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="amz-replacement-body">

                                    <div className="amz-row">
                                        <span>Reason</span>
                                        <strong>{req.reason || "—"}</strong>
                                    </div>

                                    <div className="amz-row">
                                        <span>Customer Comment</span>
                                        <strong>{req.comment || "—"}</strong>
                                    </div>

                                    <div className="amz-row">
                                        <span>Admin Response</span>
                                        <strong>{req.adminRemark || "Pending"}</strong>
                                    </div>

                                </div>

                                {req.images?.length > 0 && (
                                    <div className="amz-replacement-images">
                                        {req.images.map((img, i) => (
                                            <div key={i} className="amz-image-box">
                                                <img src={img} alt={`replacement-${i}`} />
                                                <button onClick={() => handleDownload(img, i)}>
                                                    Download
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                            </div>
                        ))}
                    </div>
                )}

                {item.isReplacement && (
                    <div className="amz-replacement-link">

                        <div>
                            <span className="amz-badge">Replacement Item</span>
                            <p>This item replaces another order item</p>
                        </div>

                        <button
                            className="amz-link-btn"
                            onClick={() => navigate(`/order-item/${item.parentOrderItemId}`)}
                        >
                            View Original
                        </button>

                    </div>
                )}

                {showPickupModal && (
                    <div className="amz-modal-overlay">
                        <div className="amz-modal">

                            <h3>Schedule Pickup</h3>

                            <div className="amz-field">
                                <label>Pickup Date</label>
                                <input
                                    type="date"
                                    onChange={(e) => setPickupDate(e.target.value)}
                                />
                            </div>

                            <div className="amz-modal-actions">
                                <button className="amz-btn primary" onClick={submitPickup}>
                                    Confirm
                                </button>

                                <button onClick={() => setShowPickupModal(false)}>
                                    Cancel
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                {showReplacementModal && (
                    <div className="amz-modal-overlay">
                        <div className="amz-modal">

                            <h3>
                                {replacementAction === "approved"
                                    ? "Approve Replacement"
                                    : "Reject Replacement"}
                            </h3>

                            <div className="amz-field">
                                <label>Admin Remark</label>
                                <textarea
                                    placeholder="Enter reason..."
                                    value={replacementReason}
                                    onChange={(e) => setReplacementReason(e.target.value)}
                                    rows={4}
                                />
                            </div>

                            <div className="amz-modal-actions">
                                <button
                                    className={`amz-btn ${replacementAction === "approved" ? "success" : "danger"}`}
                                    onClick={submitReplacementAction}
                                >
                                    Submit
                                </button>

                                <button onClick={() => setShowReplacementModal(false)}>
                                    Cancel
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                {item?.shipmentItems?.length > 0 &&
                    ["replacement_requested", "replacement_approved", "pickup_scheduled", "picked_up"].includes(itemStatus) && (
                        <div className="admin-order-item-card">
                            <h3>Status Actions</h3>

                            <div className="admin-order-item-actions">
                                {allowedActions.map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            if (status === 'pickup_scheduled') {
                                                handlePickupSchedule(status);
                                            }
                                            else if (status === 'replacement_requested') {
                                                openReplacementAction(status);
                                            } else {
                                                updateStatus(status);
                                            }
                                        }}
                                    >
                                        {status.replaceAll('_', ' ')}
                                    </button>
                                ))}
                            </div>
                        </div>)}

                {item?.shipmentItems?.length > 0 &&
                    ["replacement_requested", "replacement_approved", "pickup_scheduled", "picked_up"].includes(itemStatus) && (
                        <div className="admin-order-item-card">
                            <h3>Status Actions</h3>

                            <div className="admin-order-item-actions">
                                {allowedActions.map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            if (status === 'pickup_scheduled') {
                                                handlePickupSchedule(status);
                                                return;
                                            }

                                            if (itemStatus === "replacement_requested") {
                                                const requestId = item?.replacementRequests?.[0]?.id;

                                                if (!requestId) {
                                                    alert("No replacement request found");
                                                    return;
                                                }

                                                openReplacementAction(requestId, status); // ✅ FIXED
                                                return;
                                            }

                                            updateStatus(status);
                                        }}
                                    >
                                        {status.replaceAll('_', ' ')}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                {['confirmed', 'replacement_approved'].includes(itemStatus) && (
                    <div className="admin-order-item-card">
                        <h3>Critical Actions</h3>
                        <button className="admin-order-item-danger-btn" onClick={() => updateOrderItemStatus("cancelled")}>
                            Cancel Item
                        </button>

                    </div>
                )}

                <div className="admin-order-item-card">
                    <h3>Refunds</h3>
                    {item.refunds && item.refunds.length > 0 ? (
                        item.refunds.map(refund => (
                            <div key={refund.id} className="refund-card">
                                <p><strong>Amount:</strong> ₹{refund.refundAmount}</p>
                                <p><strong>Status:</strong> {refund.refundStatus.replaceAll('_', ' ')}</p>
                                {refund.refundStatus !== 'SUCCESS' && (
                                    <button
                                        onClick={() => handleRetryRefund(refund.id)}
                                        disabled={retryingRefundId === refund.id}
                                    >
                                        {retryingRefundId === refund.id ? 'Retrying...' : 'Retry Refund'}
                                    </button>
                                )}

                            </div>
                        ))
                    ) : (
                        <p>No refunds found</p>
                    )}
                </div>

                <BranchModal
                    show={showBranchModal}
                    onClose={() => setShowBranchModal(false)}
                    branches={branches}
                    onSelect={handleBranchSelect}
                />
            </div> */}

        </>
    );
};

export default OrderItemDetails;

